/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef } from 'react'
import { css } from '@emotion/react'
import { Link } from 'gatsby'
import { func, string } from 'prop-types'
import { useSelector } from 'react-redux'

import HomeIcon from 'gatsby-theme-marketplace-base/src/assets/icons/home.inline.svg'
import CloseIcon from 'gatsby-theme-marketplace-base/src/assets/icons/close.inline.svg'

import useIsomorphicLayoutEffect from 'gatsby-theme-marketplace-base/src/hooks/useIsomorphicLayoutEffect'
import useHydrationContext from 'gatsby-theme-marketplace-base/src/hooks/useHydrationContext'
import DevLogout from 'gatsby-theme-marketplace-base/src/components/Sidebar/DevLogout'
import Search from 'gatsby-theme-marketplace-base/src/components/Navigation/Search'
import Categories from 'gatsby-theme-marketplace-base/src/components/Sidebar/Categories'
import { Divider } from 'gatsby-theme-marketplace-base/src/components/Divider'
import { isAuthorized } from 'gatsby-theme-marketplace-base/src/store/selectors'
import { isDev } from 'gatsby-theme-marketplace-base/src/utils'

import mediaQueries from '../layout/media-queries'
import AppSectionsLinks from './Sidebar/AppSectionLinks'
import OtherSectionsLinks from './Sidebar/OtherSectionsLinks'

const sidebarCss = (theme) => css`
  display: block;
  position: fixed;
  top: 0;
  bottom: 0;
  padding: 0;
  width: 100%;
  max-width: ${theme.sidebar.maxWidth};
  background: ${theme.colors.gray50};
  z-index: 1000;
  overflow-y: auto;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 5px;
    height: 52px;
    line-height: 52px;
    padding: 0 16px;
    color: ${theme.colors.gray900};
    border-bottom: 1px solid ${theme.colors.gray100};

    > span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > div {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }

  .sidebar-categories {
    display: block;
    overflow-y: auto;
    margin-bottom: 52px;
    padding-bottom: 20px;

    a {
      display: flex;
      align-items: center;
      margin: 14px 0 14px 25px;
      color: ${theme.colors.black};
      text-decoration: none;
      font-size: 16px;
      line-height: 28px;

      &:hover {
        color: ${theme.colors.blue500};
      }

      > svg {
        flex-shrink: 0;
        margin-right: 10px;
        margin-top: -4px;
      }

      &.active-link {
        color: ${theme.colors.blue500};
        font-weight: ${theme.font.bold};

        > svg > path {
          fill: ${theme.colors.blue500};
        }
      }
    }

    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
  }
`

const searchbarCss = (isExpanded) => css`
  .autocomplete {
    width: ${isExpanded ? '100%' : 'auto'};

    > .autocomplete-results {
      width: 100%;
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 13px;
    }

    .autocomplete-item__name {
      font-weight: 500;
      line-height: 24px;
    }

    .autocomplete-item__category {
      line-height: 14px;
    }

    .autocomplete-list {
      > .autocomplete-item:last-child {
        margin-bottom: 0;
      }
    }
  }

  li.autocomplete-item.autocomplete-item {
    &:last-child {
      margin-bottom: 0px;
    }
  }

  .autocomplete-item__info {
    overflow: hidden;
  }
`

const closeIconCss = (theme) => css`
  color: ${theme.colors.gray900};
  display: flex;
  align-items: center;
  position: relative;
  right: -4px;
  margin-left: 10px;

  ${mediaQueries.smallScreen(-280)} {
    display: none;
  }
`

const dividerCss = css`
  margin: 8px 16px;
  width: calc(100% - 32px);
`

const dividerMarginCss = (margin) => css`
  margin: ${margin};
`

const headerActionsWrapper = (isExpanded) => css`
  width: ${isExpanded ? '100%' : 'auto'};

  ${searchbarCss(isExpanded)};
`

const MainSidebar = ({ onToggleSidebar, title = 'Marketplace' }) => {
  const searchInputRef = useRef(null)
  const [isSearchExpanded, setIsExpanded] = useState(false)
  const hydrated = useHydrationContext()
  const isUserAuthorized = useSelector(isAuthorized)

  useIsomorphicLayoutEffect(() => {
    if (isSearchExpanded && searchInputRef?.current) {
      searchInputRef.current.focus()
    }
  }, [isSearchExpanded])

  return (
    <aside css={(theme) => sidebarCss(theme)}>
      <div className="header">
        {!isSearchExpanded && <span>{title}</span>}

        <div css={headerActionsWrapper(isSearchExpanded)}>
          <Search
            isInputVisible={isSearchExpanded}
            isExpanded={isSearchExpanded}
            onIsExpanded={setIsExpanded}
            className="autocomplete"
            placeholder="Search in Marketplace..."
            displayAAVersion
            searchInputRef={searchInputRef}
          />

          {!isSearchExpanded && (
            <a
              css={closeIconCss}
              href="#"
              onClick={(e) => {
                e.preventDefault()
                onToggleSidebar()
              }}
            >
              <CloseIcon css={(theme) => `fill: ${theme.colors.gray900};`} />
            </a>
          )}
        </div>
      </div>

      <div className="sidebar-categories">
        <Link to="/" activeClassName="active-link">
          <HomeIcon />
          <span>Explore</span>
        </Link>

        <Divider css={[dividerCss, dividerMarginCss('0 16px 0 16px')]} />

        <Categories />

        <Divider css={[dividerCss, dividerMarginCss('0 16px 8px 16px')]} />

        <OtherSectionsLinks />

        <Divider css={dividerCss} />

        <AppSectionsLinks />

        {hydrated && isDev && isUserAuthorized && (
          <>
            <Divider css={dividerCss} />
            <DevLogout />
          </>
        )}
      </div>
    </aside>
  )
}

MainSidebar.propTypes = {
  onToggleSidebar: func,
  title: string,
}

export default MainSidebar
